/**
 * UI Constants for maintaining consistency across the application
 * This file contains all text constants, tooltips, messages, and translations
 * Future: This will serve as the foundation for internationalization (i18n)
 */

// Application-wide text constants
export const UI_CONSTANTS = {
  // Application branding
  APP: {
    NAME: "AlphaCore",
    SUBTITLE: "Case Management System",
    COPYRIGHT: "© 2025 Alpha Health. All rights reserved.",
  },

  // Page titles
  TITLES: {
    DASHBOARD: "Case Management Dashboard",
    CASE_MANAGEMENT: "Case Management",
    CASE_DETAILS: "Case Details",
    PROCESSING_QUEUE: "Processing Queue",
    DOCUMENT_LIBRARY: "Document Library",
    DOCUMENT_UPLOAD: "Document Upload",
    COMPLIANCE_RULES: "Compliance Rules",
    COMPLIANCE_MANAGEMENT: "Compliance Management",
    SYSTEM_ADMINISTRATION: "System Administration",
    DOCUMENT_TYPES: "Document Types",
    EXTRACTION_PROMPTS: "Extraction Prompts",
    API_DEBUG: "API Debug",
    LOGIN: "Sign In",
    USER_MANAGEMENT: "User Management",
  },

  // Page descriptions/subtitles
  DESCRIPTIONS: {
    DASHBOARD:
      "Monitor case metrics, track performance, and manage your compliance workload effectively",
    CASE_MANAGEMENT:
      "Manage and track compliance cases throughout their lifecycle",
    CASE_DETAILS: "View detailed information about the selected case",
    PROCESSING_QUEUE: "Monitor document processing jobs and view results",
    DOCUMENT_LIBRARY: "Browse and manage your document collection",
    DOCUMENT_UPLOAD: "Upload and organize documents for compliance review",
    COMPLIANCE_RULES: "Configure and manage compliance validation rules",
    SYSTEM_ADMINISTRATION: "System configuration and administrative tools",
    LOGIN: "Enter your credentials to access your account",
  },

  // Navigation and actions
  ACTIONS: {
    SIGN_IN: "Sign In",
    SIGN_OUT: "Sign Out",
    BACK: "Back",
    CANCEL: "Cancel",
    SAVE: "Save",
    EDIT: "Edit",
    DELETE: "Delete",
    CREATE: "Create",
    UPDATE: "Update",
    SUBMIT: "Submit",
    REFRESH: "Refresh",
    SEARCH: "Search",
    FILTER: "Filter",
    EXPORT: "Export",
    IMPORT: "Import",
    UPLOAD: "Upload",
    DOWNLOAD: "Download",
    VIEW: "View",
    PREVIEW: "Preview",
    COPY: "Copy",
    CLOSE: "Close",
    CONFIRM: "Confirm",
    RETRY: "Retry",
    LOAD_MORE: "Load More",
    SHOW_MORE: "Show More",
    SHOW_LESS: "Show Less",
    SELECT_ALL: "Select All",
    CLEAR_ALL: "Clear All",
    RESET: "Reset",
    APPLY: "Apply",
    AUTO_FILL: "Auto-Fill Credentials",
    RE_RUN_EVALUATION: "Re-run Evaluation",
  },

  // Form labels
  LABELS: {
    EMAIL: "Email Address",
    PASSWORD: "Password",
    FIRST_NAME: "First Name",
    LAST_NAME: "Last Name",
    FULL_NAME: "Full Name",
    PHONE: "Phone Number",
    ADDRESS: "Address",
    CITY: "City",
    STATE: "State",
    ZIP_CODE: "ZIP Code",
    COUNTRY: "Country",
    TITLE: "Title",
    DESCRIPTION: "Description",
    PRIORITY: "Priority",
    STATUS: "Status",
    ASSIGNED_TO: "Assigned To",
    DUE_DATE: "Due Date",
    CREATED_DATE: "Created Date",
    UPDATED_DATE: "Updated Date",
    TAGS: "Tags",
    COMMENTS: "Comments",
    ATTACHMENTS: "Attachments",
    FILE_NAME: "File Name",
    FILE_SIZE: "File Size",
    FILE_TYPE: "File Type",
    DOCUMENT_TYPE: "Document Type",
    CONFIDENCE: "Confidence",
    DURATION: "Duration",
    JOB_ID: "Job ID",
    FILE_ID: "File ID",
  },

  // Status values
  STATUS: {
    OPEN: "Open",
    IN_PROGRESS: "In Progress",
    RESOLVED: "Resolved",
    CLOSED: "Closed",
    PENDING: "Pending",
    COMPLETED: "Completed",
    FAILED: "Failed",
    PROCESSING: "Processing",
    DRAFT: "Draft",
    PUBLISHED: "Published",
    ARCHIVED: "Archived",
    ACTIVE: "Active",
    INACTIVE: "Inactive",
    ENABLED: "Enabled",
    DISABLED: "Disabled",
  },

  // Priority values
  PRIORITY: {
    CRITICAL: "Critical",
    HIGH: "High",
    MEDIUM: "Medium",
    LOW: "Low",
  },

  // Common messages
  MESSAGES: {
    LOADING: "Loading...",
    SAVING: "Saving...",
    UPLOADING: "Uploading...",
    PROCESSING: "Processing...",
    SUCCESS: "Success!",
    ERROR: "An error occurred",
    NO_DATA: "No data available",
    NO_RESULTS: "No results found",
    SEARCH_PLACEHOLDER: "Search...",
    FILTER_PLACEHOLDER: "Filter by...",
    REQUIRED_FIELD: "This field is required",
    INVALID_EMAIL: "Please enter a valid email address",
    INVALID_PASSWORD: "Password must be at least 8 characters",
    PASSWORDS_DONT_MATCH: "Passwords do not match",
    UNAUTHORIZED: "You are not authorized to perform this action",
    SESSION_EXPIRED: "Your session has expired. Please sign in again.",
    NETWORK_ERROR: "Network error. Please check your connection.",
    FILE_TOO_LARGE: "File size exceeds maximum limit",
    UNSUPPORTED_FILE_TYPE: "Unsupported file type",
    CONFIRM_DELETE: "Are you sure you want to delete this item?",
    CONFIRM_LOGOUT: "Are you sure you want to sign out?",
    CHANGES_SAVED: "Changes saved successfully",
    CHANGES_DISCARDED: "Changes discarded",
    ITEM_CREATED: "Item created successfully",
    ITEM_UPDATED: "Item updated successfully",
    ITEM_DELETED: "Item deleted successfully",
    OPERATION_COMPLETED: "Operation completed successfully",
    OPERATION_FAILED: "Operation failed",
  },

  // Tooltips and help text
  TOOLTIPS: {
    BACK_TO_CASES: "Go back to case list",
    REFRESH_DATA: "Refresh the current data",
    EXPORT_DATA: "Export data to file",
    SORT_ASCENDING: "Sort in ascending order",
    SORT_DESCENDING: "Sort in descending order",
    VIEW_DETAILS: "View detailed information",
    EDIT_ITEM: "Edit this item",
    DELETE_ITEM: "Delete this item",
    COPY_TO_CLIPBOARD: "Copy to clipboard",
    TOGGLE_PASSWORD: "Show/hide password",
    SELECT_FILE: "Select file to upload",
    REQUIRED_FIELD: "This field is required",
    OPTIONAL_FIELD: "This field is optional",
    SEARCH_HELP: "Enter keywords to search",
    FILTER_HELP: "Select options to filter results",

    // Dashboard specific
    TOTAL_CASES:
      "Total number of cases in the system across all statuses and priority levels",
    OVERDUE_CASES:
      "Cases that have passed their due date and require immediate attention to prevent compliance issues",
    RESOLVED_TODAY:
      "Number of cases resolved or closed today, indicating daily productivity and progress",
    AVG_RESOLUTION_TIME:
      "Average time taken to resolve cases from creation to closure, helping measure team efficiency",
    STATUS_OVERVIEW: "Distribution of all cases by their current status",
    PRIORITY_DISTRIBUTION:
      "Cases categorized by priority level and expected resolution timeframe",
    RECENT_CASES:
      "Latest cases created in the system, sorted by creation date. Click on any row to view case details.",

    // Processing Queue specific
    JOB_STATUS: "Current processing status of the job",
    CONFIDENCE_SCORE: "AI confidence level for document processing",
    PROCESSING_DURATION: "Time taken to process the document",
    VIEW_RESULTS: "View processing results and extracted data",
  },

  // Error messages
  ERRORS: {
    GENERIC: "Something went wrong. Please try again.",
    NETWORK:
      "Unable to connect to the server. Please check your internet connection.",
    VALIDATION: "Please check the form for errors.",
    UNAUTHORIZED: "You do not have permission to perform this action.",
    NOT_FOUND: "The requested resource was not found.",
    SERVER_ERROR: "Server error. Please try again later.",
    TIMEOUT: "Request timed out. Please try again.",
    FILE_UPLOAD: "Failed to upload file. Please try again.",
    DATA_LOAD: "Failed to load data. Please refresh the page.",
    SAVE_FAILED: "Failed to save changes. Please try again.",
    DELETE_FAILED: "Failed to delete item. Please try again.",
    LOGIN_FAILED: "Invalid email or password. Please try again.",
    LOGOUT_FAILED: "Failed to sign out. Please try again.",
  },

  // Success messages
  SUCCESS: {
    LOGIN: "Successfully signed in",
    LOGOUT: "Successfully signed out",
    SAVE: "Changes saved successfully",
    CREATE: "Item created successfully",
    UPDATE: "Item updated successfully",
    DELETE: "Item deleted successfully",
    UPLOAD: "File uploaded successfully",
    EXPORT: "Data exported successfully",
    IMPORT: "Data imported successfully",
    EMAIL_SENT: "Email sent successfully",
    PASSWORD_RESET: "Password reset successfully",
    PROFILE_UPDATED: "Profile updated successfully",
  },

  // Table headers and columns
  TABLE: {
    CASE_DETAILS: "Case Details",
    PRIORITY: "Priority",
    STATUS: "Status",
    ASSIGNED_TO: "Assigned To",
    CREATED: "Created",
    DUE_DATE: "Due Date",
    ACTIONS: "Actions",
    JOB_ID: "Job ID",
    FILE_ID: "File ID",
    DOCUMENT_TYPE: "Document Type",
    CONFIDENCE: "Confidence",
    DURATION: "Duration",
    FILE_NAME: "File Name",
    FILE_SIZE: "File Size",
    UPDATED: "Updated",
    CLICK_ROW_TO_VIEW: "Click row to view details",
  },

  // Dashboard metrics
  METRICS: {
    ALL_TIME: "All time",
    DAILY_PROGRESS: "Daily progress",
    PERFORMANCE_METRIC: "Performance metric",
    IMMEDIATE_ACTION_REQUIRED: "Immediate action required",
    FROM_LAST_PERIOD: "from last period",
  },

  // Time and date formats
  TIME: {
    DAYS_AGO: (count: number) =>
      count === 1 ? "1 day ago" : `${count} days ago`,
    HOURS_AGO: (count: number) =>
      count === 1 ? "1 hour ago" : `${count} hours ago`,
    MINUTES_AGO: (count: number) =>
      count === 1 ? "1 minute ago" : `${count} minutes ago`,
    JUST_NOW: "Just now",
    NO_DUE_DATE: "No due date",
    OVERDUE: "Overdue",
    AUTO_REFRESH: "Auto-refreshing every 5 seconds",
  },

  // Priority descriptions
  PRIORITY_DESCRIPTIONS: {
    CRITICAL: "Immediate attention required",
    HIGH: "Resolve within 24 hours",
    MEDIUM: "Resolve within 3 days",
    LOW: "Resolve within 1 week",
  },

  // Accessibility labels
  A11Y: {
    MAIN_CONTENT: "Main content",
    NAVIGATION: "Main navigation",
    SEARCH: "Search",
    USER_MENU: "User menu",
    BREADCRUMB: "Breadcrumb navigation",
    PAGINATION: "Pagination navigation",
    SORT_BUTTON: "Sort table by column",
    FILTER_BUTTON: "Filter table data",
    LOADING_CONTENT: "Loading content",
    ERROR_MESSAGE: "Error message",
    SUCCESS_MESSAGE: "Success message",
    WARNING_MESSAGE: "Warning message",
    INFO_MESSAGE: "Information message",
    CLOSE_DIALOG: "Close dialog",
    EXPAND_MENU: "Expand menu",
    COLLAPSE_MENU: "Collapse menu",
    TOGGLE_THEME: "Toggle theme",
    SKIP_TO_CONTENT: "Skip to main content",
  },

  // Demo and development
  DEMO: {
    SUPERADMIN_CREDENTIALS: "SuperAdmin Credentials:",
    EMAIL_COPIED: "Email copied!",
    PASSWORD_COPIED: "Password copied!",
    COPY_EMAIL: "Copy email",
    COPY_PASSWORD: "Copy password",
  },
} as const;

// View mode constants for pages with card/table toggle
export const VIEW_MODE = {
  GRID: "grid",
  TABLE: "table",
} as const;

// Tenant category names as returned by the backend login response
export const TENANT_CATEGORIES = {
  ACCOUNT_MANAGER: "Account Manager",
} as const;

// Type definitions for better TypeScript support
export type UIConstantKeys = keyof typeof UI_CONSTANTS;
export type TitleKeys = keyof typeof UI_CONSTANTS.TITLES;
export type ActionKeys = keyof typeof UI_CONSTANTS.ACTIONS;
export type MessageKeys = keyof typeof UI_CONSTANTS.MESSAGES;
export type TooltipKeys = keyof typeof UI_CONSTANTS.TOOLTIPS;

// Helper functions for getting translations
export const getTitle = (key: TitleKeys): string => UI_CONSTANTS.TITLES[key];
export const getAction = (key: ActionKeys): string => UI_CONSTANTS.ACTIONS[key];
export const getMessage = (key: MessageKeys): string =>
  UI_CONSTANTS.MESSAGES[key];
export const getTooltip = (key: TooltipKeys): string =>
  UI_CONSTANTS.TOOLTIPS[key];

// Future: Internationalization helper
export const t = (key: string, params?: Record<string, any>): string => {
  // This will be expanded in the future to support multiple languages
  // For now, it just returns the English text
  const keys = key.split(".");
  let value: any = UI_CONSTANTS;

  for (const k of keys) {
    value = value?.[k];
  }

  if (typeof value === "function" && params) {
    return value(params);
  }

  return value || key;
};

export default UI_CONSTANTS;
